const products = [
  {
    id: "vpn",
    name: "VPN",
    tagline: "Secure &amp; Private Browsing",
    description:
      "A high-performance VPN application engineered for iOS. Encrypts your internet connection with military-grade protocols, masks your digital identity, and keeps your data private on any network — public or private.",
    features: ["256-bit AES Encryption", "Zero-Log Policy", "Global Server Network", "Kill Switch Protection"],
    badge: "Security",
    badgeIcon: "ri-shield-check-line",
    bgDark: true,
    img: "https://public.readdy.ai/ai/img_res/ba11185ebd24ab3c7764886862d2fc1e.jpg",
  },
  {
    id: "cleaner",
    name: "Cleaner",
    tagline: "Optimize iOS Performance",
    description:
      "An intelligent iOS optimization suite that removes junk files, manages storage, and boosts device speed. Cleaner provides real-time analysis and one-touch performance improvements for a smoother iOS experience.",
    features: ["Smart Junk Detection", "Photo Duplicates Remover", "Contact Manager", "Storage Analyzer"],
    badge: "Performance",
    badgeIcon: "ri-speed-up-line",
    bgDark: false,
    img: "https://readdy.ai/api/search-image?query=iPhone%20smartphone%20mockup%20showing%20cleaner%20optimization%20app%20interface%20with%20clean%20minimal%20UI%20progress%20rings%20and%20storage%20charts%20on%20soft%20light%20gray%20background%2C%20product%20photography%20style%2C%20minimalist%20modern%20tech%20aesthetic%2C%20no%20text%2C%20centered%20device%2C%20soft%20lighting&width=480&height=560&seq=cleaner-product-001&orientation=portrait",
  },
];

export default function ProductsSection() {
  return (
    <section id="products" className="w-full py-24 md:py-32" style={{ backgroundColor: "#f9f8f6" }}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8 mb-16 md:mb-20">
          <div className="max-w-xl">
            <h2
              className="text-4xl md:text-5xl font-normal leading-tight text-gray-900"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Our iOS
              <br />
              <span className="italic">Products</span>
            </h2>
          </div>
          <div className="max-w-sm lg:pt-3">
            <p className="text-gray-500 text-sm leading-7" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 400 }}>
              We build specialized iOS applications focused on mobile security and device optimization. Each product is engineered with precision for reliability, speed, and user experience.
            </p>
          </div>
        </div>

        {/* Product Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {products.map((product) => (
            <article
              key={product.id}
              className={`rounded-2xl overflow-hidden flex flex-col ${
                product.bgDark ? "bg-gray-900 text-white" : "bg-white text-gray-900 border border-gray-100"
              }`}
            >
              {/* Card Top */}
              <div className="p-8 pb-0 flex-1">
                <div className="flex items-center gap-2 mb-6">
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full ${product.bgDark ? "bg-white/10" : "bg-gray-100"}`}>
                    <i className={`${product.badgeIcon} text-sm ${product.bgDark ? "text-white/70" : "text-gray-500"}`}></i>
                  </div>
                  <span
                    className={`text-xs font-semibold tracking-widest uppercase ${
                      product.bgDark ? "text-white/50" : "text-gray-400"
                    }`}
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {product.badge}
                  </span>
                </div>

                <h3
                  className={`text-3xl md:text-4xl font-normal mb-3 ${product.bgDark ? "text-white" : "text-gray-900"}`}
                  style={{ fontFamily: "'DM Serif Display', serif" }}
                >
                  {product.name}
                </h3>
                <p
                  className={`text-sm font-semibold mb-4 tracking-wide ${product.bgDark ? "text-white/60" : "text-gray-500"}`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                  dangerouslySetInnerHTML={{ __html: product.tagline }}
                ></p>
                <p
                  className={`text-sm leading-7 mb-8 ${product.bgDark ? "text-white/55" : "text-gray-500"}`}
                  style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}
                >
                  {product.description}
                </p>

                {/* Features */}
                <ul className="grid grid-cols-2 gap-3 mb-8">
                  {product.features.map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <div className={`w-4 h-4 flex items-center justify-center flex-shrink-0`}>
                        <i className={`ri-check-line text-xs ${product.bgDark ? "text-white/50" : "text-gray-400"}`}></i>
                      </div>
                      <span
                        className={`text-xs ${product.bgDark ? "text-white/60" : "text-gray-500"}`}
                        style={{ fontFamily: "'Inter', sans-serif" }}
                      >
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Card Image */}
              <div className={`relative w-full h-64 md:h-72 overflow-hidden ${product.bgDark ? "mt-2" : "mt-2"}`}>
                <img
                  src={product.img}
                  alt={`${product.name} iOS App by INTEROSMO P.C.`}
                  title={`${product.name} – iOS mobile security app`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
